/**
 * EmptyState — a small line illustration, a heading, one sentence and an optional action
 * (REDESIGN §7d). Replaces every bare "אין נתונים".
 *
 * The copy is Hebrew and belongs to each app's `i18n/he.ts`, so it arrives as props. The
 * illustration table is a module constant (`illustrations.ts`), which is why the
 * `dangerouslySetInnerHTML` here can never see user input.
 */
import type { ReactNode } from 'react';
import { illustrationMarkup } from './illustrations.ts';
import type { IllustrationName } from './illustrations.ts';
import './kit.css';

export interface EmptyStateProps {
  /** Which line illustration to draw. */
  illustration: IllustrationName;
  /** The heading — one short Hebrew phrase. */
  title: string;
  /** One sentence. Never more (§8: the reader should not have to assemble the story). */
  body?: string;
  /** A link or button — "חבר מחדש", "נקה סינון". */
  action?: ReactNode;
  /** `page` gives the block the full empty-screen padding; `inline` fits inside a card. */
  variant?: 'page' | 'inline';
  className?: string;
}

export function EmptyState({ illustration, title, body, action, variant = 'page', className }: EmptyStateProps) {
  return (
    <div className={`fk-empty fk-empty--${variant}${className ? ` ${className}` : ''}`}>
      <svg
        className="fk-illus"
        width="120"
        height="84"
        viewBox="0 0 120 84"
        aria-hidden="true"
        focusable="false"
        dangerouslySetInnerHTML={{ __html: illustrationMarkup(illustration) }}
      />
      <h3 className="fk-empty__title">{title}</h3>
      {body ? <p className="fk-empty__body">{body}</p> : null}
      {action ? <div className="fk-empty__action">{action}</div> : null}
    </div>
  );
}
